import { useState } from "react";
import { AlertTriangle, Loader2 } from "lucide-react";
import { ProviderShiftIssueType } from "@api/shared-runtime/enums";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { useReportShiftIssue } from "../hooks/useSubmitTimeEntries";

interface ReportShiftIssueDialogProps {
  shiftId: string;
}

const issueOptions = [
  {
    value: ProviderShiftIssueType.UnableToAttend,
    label: "I wasn’t able to make it",
    description: "I did not work this scheduled shift.",
  },
  {
    value: ProviderShiftIssueType.IncorrectDateOrTime,
    label: "The date or time is incorrect",
    description: "This shift was scheduled for the wrong date or time.",
  },
  {
    value: ProviderShiftIssueType.Other,
    label: "There’s another error",
    description: "Something else about this scheduled shift is incorrect.",
  },
] as const;

export function ReportShiftIssueDialog({
  shiftId,
}: ReportShiftIssueDialogProps) {
  const [open, setOpen] = useState(false);
  const [issueType, setIssueType] = useState<ProviderShiftIssueType | null>(
    null,
  );
  const [note, setNote] = useState("");
  const { mutate: reportIssue, isPending } = useReportShiftIssue();

  const requiresNote =
    issueType !== null && issueType !== ProviderShiftIssueType.UnableToAttend;
  const canSubmit = issueType !== null && (!requiresNote || note.trim() !== "");

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen && !isPending) {
      setIssueType(null);
      setNote("");
    }
  };

  const handleSubmit = () => {
    if (!issueType || !canSubmit) {
      return;
    }

    reportIssue(
      {
        shiftId,
        issueType,
        note: note.trim() || undefined,
      },
      {
        onSuccess: () => setOpen(false),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="text-slate-500 hover:text-amber-700"
        >
          <AlertTriangle className="mr-2 h-4 w-4" />I didn’t work / Report an
          issue
        </Button>
      </DialogTrigger>

      <DialogContent className="w-full max-w-lg">
        <DialogHeader>
          <DialogTitle>Why doesn’t this shift need a timesheet?</DialogTitle>
          <DialogDescription className="text-sm text-slate-600">
            Choose the reason so the shift can be removed from your timesheet
            list.
          </DialogDescription>
        </DialogHeader>

        <RadioGroup
          value={issueType ?? undefined}
          onValueChange={(value) =>
            setIssueType(value as ProviderShiftIssueType)
          }
          className="space-y-2 py-2"
        >
          {issueOptions.map((option) => (
            <Label
              key={option.value}
              htmlFor={`shift-issue-${shiftId}-${option.value}`}
              className="flex cursor-pointer items-start gap-3 rounded-lg border p-3 hover:bg-slate-50"
            >
              <RadioGroupItem
                id={`shift-issue-${shiftId}-${option.value}`}
                value={option.value}
                className="mt-0.5 shrink-0"
              />
              <span>
                <span className="block text-sm font-medium text-slate-900">
                  {option.label}
                </span>
                <span className="block text-sm font-normal text-slate-500">
                  {option.description}
                </span>
              </span>
            </Label>
          ))}
        </RadioGroup>

        {issueType && (
          <div className="space-y-2">
            <Label htmlFor={`shift-issue-note-${shiftId}`}>
              Details {requiresNote ? "(required)" : "(optional)"}
            </Label>
            <Textarea
              id={`shift-issue-note-${shiftId}`}
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder={
                requiresNote
                  ? "Tell the organization what needs to be corrected."
                  : "Add any details you want the organization to know."
              }
              maxLength={2000}
              rows={4}
            />
          </div>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => setOpen(false)}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit || isPending}
          >
            {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {issueType === ProviderShiftIssueType.UnableToAttend
              ? "Mark as not worked"
              : "Report issue"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
